import { useState } from "react";

import { Button } from "@/design-system/primitives";
import { ApiError } from "@/lib/api/client";
import {
  useClinicalTeam,
  usePatientAssignment,
  useReassignPatient,
} from "@/lib/api/clinicianHooks";
import { useBootstrap } from "@/lib/api/hooks";
import type {
  AssignedClinician,
  PatientAssignmentOverview,
  PatientAssignmentPrescriber,
  PrescriptionWorkItem,
} from "@/types/clinician";

/** Short label for a worklist row whose patient has no usable prescriber. */
export function assignmentIssueLabel(
  item: Pick<PrescriptionWorkItem, "assignment_issue" | "patient_state">,
): string | null {
  if (item.assignment_issue === "unassigned") return "Unassigned";
  if (item.assignment_issue === "assigned_prescriber_not_licensed") {
    return `Prescriber not licensed in ${item.patient_state || "patient's state"}`;
  }
  return null;
}

function clinicianLabel(
  prescriber: Pick<PatientAssignmentPrescriber, "display_name" | "email">,
) {
  return prescriber.display_name || prescriber.email || "Unnamed prescriber";
}

function eligibilityNote(
  prescriber: PatientAssignmentPrescriber,
  state: string | null,
): string {
  if (prescriber.current) return "Current";
  if (!prescriber.active) return "Inactive";
  if (!prescriber.licensed_for_patient) {
    return state ? `Not licensed in ${state}` : "Patient's state unknown";
  }
  if (prescriber.unrestricted) return "No state limits recorded";
  return state ? `Licensed in ${state}` : "Licensed";
}

/**
 * The patient's primary prescriber, with a supervisor-only reassign panel
 * while team routing is on. Only prescribers licensed in the patient's state
 * can be chosen; the backend enforces the same rule.
 */
export function PrescriberAssignment({
  patientId,
  assigned,
  patientState,
  assignmentIssue,
}: {
  patientId: string;
  assigned: AssignedClinician | null;
  patientState?: string | null;
  assignmentIssue?: PrescriptionWorkItem["assignment_issue"];
}) {
  const { data: bootstrap } = useBootstrap();
  const supervisor =
    bootstrap?.admin_role === "admin" ||
    bootstrap?.admin_role === "clinical_supervisor";
  const team = useClinicalTeam(supervisor);
  const canReassign = supervisor && Boolean(team.data?.team_mode_enabled);
  const [open, setOpen] = useState(false);
  const issue = assignmentIssueLabel({
    assignment_issue: assignmentIssue ?? null,
    patient_state: patientState ?? null,
  });

  if (!assigned && !issue && !canReassign) return null;

  return (
    <div className="flex flex-col gap-6">
      <p className="type-label-xs text-ink-muted flex flex-wrap items-center gap-6">
        <span>
          {assigned
            ? `Prescriber · ${assigned.display_name || assigned.email}`
            : "No prescriber assigned"}
          {patientState ? ` · ${patientState}` : ""}
        </span>
        {issue && (
          <span className="px-5 py-2 border border-danger/30 bg-danger-wash text-danger">
            {issue}
          </span>
        )}
        {canReassign && (
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="underline underline-offset-2 hover:text-ink"
          >
            {open ? "Close" : "Change"}
          </button>
        )}
      </p>
      {canReassign && open && <ReassignPanel patientId={patientId} />}
    </div>
  );
}

function ReassignPanel({ patientId }: { patientId: string }) {
  const overview = usePatientAssignment(patientId);
  const reassign = useReassignPatient(patientId);
  const [choice, setChoice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (overview.isLoading) {
    return (
      <div className="border border-line-subtle bg-sunken p-10 type-body-sm text-ink-muted">
        Loading prescribers…
      </div>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <div className="border border-danger/30 bg-danger-wash p-10 type-body-sm text-danger">
        Prescriber assignment could not be loaded.
      </div>
    );
  }
  const data = overview.data;
  const selected = data.prescribers.find(
    (prescriber) => prescriber.clinician_id === choice,
  );

  return (
    <div className="border border-line-subtle bg-sunken p-10 flex flex-col gap-8 max-w-[44rem]">
      <p className="type-body-sm text-ink-muted">
        {data.patient_state
          ? `Patient state: ${data.patient_state}. Only prescribers licensed there can take this patient.`
          : "The patient's state is unknown, so only prescribers without recorded state limits can take this patient."}
        {data.assignment && !data.current_licensed
          ? " The current prescriber is not licensed there."
          : ""}
      </p>
      <div
        className="flex flex-col divide-y divide-line-subtle border border-line-subtle bg-raised"
        role="radiogroup"
        aria-label="Primary prescriber"
      >
        {data.prescribers.map((prescriber) => {
          const disabled = prescriber.current || !prescriber.eligible;
          return (
            <label
              key={prescriber.clinician_id}
              className={`flex items-center justify-between gap-10 px-10 py-7 type-body-sm ${disabled ? "text-ink-muted" : "cursor-pointer hover:bg-page"}`}
            >
              <span className="inline-flex items-center gap-7 min-w-0">
                <input
                  type="radio"
                  name={`prescriber-${patientId}`}
                  disabled={disabled}
                  checked={choice === prescriber.clinician_id}
                  onChange={() => {
                    setChoice(prescriber.clinician_id);
                    setError(null);
                    setSaved(false);
                  }}
                />
                <span className="truncate">{clinicianLabel(prescriber)}</span>
              </span>
              <span
                className={`type-label-xs shrink-0 ${prescriber.eligible || prescriber.current ? "text-ink-muted" : "text-danger"}`}
              >
                {eligibilityNote(prescriber, data.patient_state)}
              </span>
            </label>
          );
        })}
        {!data.prescribers.length && (
          <p className="px-10 py-8 type-body-sm text-ink-muted">
            No prescribers are on the clinical team yet.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-8">
        <Button
          type="button"
          size="sm"
          disabled={!selected || reassign.isPending}
          loading={reassign.isPending}
          onClick={async () => {
            if (!selected) return;
            setError(null);
            try {
              await reassign.mutateAsync(selected.clinician_id);
              setSaved(true);
              setChoice(null);
            } catch (cause) {
              setError(
                cause instanceof ApiError
                  ? cause.message
                  : "The prescriber could not be changed.",
              );
            }
          }}
        >
          {selected
            ? `Assign to ${clinicianLabel(selected)}`
            : "Choose a prescriber"}
        </Button>
        {saved && (
          <span className="type-body-sm text-success">
            Prescriber updated. Their worklist and Healthie inbox follow.
          </span>
        )}
        {error && <span className="type-body-sm text-danger">{error}</span>}
      </div>
      <AssignmentHistory data={data} />
    </div>
  );
}

function AssignmentHistory({ data }: { data: PatientAssignmentOverview }) {
  if (!data.history.length) return null;
  const names = new Map(
    data.prescribers.map((prescriber) => [
      prescriber.clinician_id,
      clinicianLabel(prescriber),
    ]),
  );
  const name = (id: string | null) =>
    id ? (names.get(id) ?? "Former clinician") : "None";
  return (
    <details className="type-label-xs text-ink-muted">
      <summary className="cursor-pointer">Assignment history</summary>
      <ul className="mt-6 flex flex-col gap-3">
        {data.history.slice(0, 10).map((event, index) => (
          <li key={`${event.created_at}-${index}`}>
            {new Date(event.created_at).toLocaleString()} ·{" "}
            {name(event.from_clinician_id)} → {name(event.to_clinician_id)} ·{" "}
            {event.source}
            {event.actor ? ` · ${event.actor}` : ""}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Patient-record variant: supervisors see the current prescriber and can
 *  reassign; it renders nothing for others or in single-clinician mode. */
export function PatientPrescriber({ patientId }: { patientId: string }) {
  const { data: bootstrap } = useBootstrap();
  const supervisor =
    bootstrap?.admin_role === "admin" ||
    bootstrap?.admin_role === "clinical_supervisor";
  const team = useClinicalTeam(supervisor);
  const teamMode = supervisor && Boolean(team.data?.team_mode_enabled);
  const overview = usePatientAssignment(patientId, teamMode);
  if (!teamMode || !overview.data) return null;
  const current = overview.data.prescribers.find(
    (prescriber) => prescriber.current,
  );
  return (
    <PrescriberAssignment
      patientId={patientId}
      assigned={
        current
          ? {
              clinician_id: current.clinician_id,
              display_name: current.display_name,
              email: current.email,
            }
          : null
      }
      patientState={overview.data.patient_state}
      assignmentIssue={
        !overview.data.assignment
          ? "unassigned"
          : overview.data.current_licensed
            ? null
            : "assigned_prescriber_not_licensed"
      }
    />
  );
}
